import { Button, Flex, Typography } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Group, Panel, Separator } from "react-resizable-panels";

import type { Endpoint, RequestRecord } from "sandbox/types";

import { useMediaQuery } from "design-system/hooks";
import { useSandboxEndpoints } from "sandbox/api/hooks/useSandboxQueries";
import { setEndpointCache } from "sandbox/hooks/useFindEndpoint";
import { useRequestHistory } from "sandbox/hooks/useRequestHistory";

import { EndpointList } from "./EndpointList";
import s from "./Explorer.module.css";
import { RecentRequests } from "./RecentRequests";
import { RequestPane } from "./RequestPane";

export const Explorer = () => {
  const { data: groups, isLoading } = useSandboxEndpoints();
  const { entries, record } = useRequestHistory();
  const [selected, setSelected] = useState<Endpoint | undefined>();
  const [isHistorySidebarVisible, setIsHistorySidebarVisible] = useState(false);
  const { t } = useTranslation("sandbox");
  const isMobile = useMediaQuery("(max-width: 48em)");

  const layout = isMobile
    ? {
        orientation: "vertical" as const,
        endpointDefaultSize: 200,
        endpointMinSize: 140,
        endpointMaxSize: 320,
      }
    : {
        orientation: "horizontal" as const,
        endpointDefaultSize: 260,
        endpointMinSize: 180,
        endpointMaxSize: 600,
      };

  const recentRequestsLabel = isHistorySidebarVisible
    ? t("components.recentRequests.hide")
    : t("components.recentRequests.show");

  const endpoints = useMemo(() => {
    if (!groups) return [];
    const all = groups.flatMap(g => g.endpoints as unknown as Endpoint[]);
    setEndpointCache(groups);
    return all;
  }, [groups]);

  const handleSelectEntry = (entry: RequestRecord) => {
    const matched = endpoints.find(e => e.id === entry.endpointId);
    if (matched) setSelected(matched);
  };

  const hasEndpoints = !isLoading && endpoints.length > 0;

  return (
    <div className={s.root}>
      {hasEndpoints ? (
        <Group orientation={layout.orientation} className={s.group}>
          <Panel
            defaultSize={layout.endpointDefaultSize}
            minSize={layout.endpointMinSize}
            maxSize={layout.endpointMaxSize}
          >
            <EndpointList
              groups={groups ?? []}
              selected={selected}
              onSelect={setSelected}
            />
          </Panel>
          <Separator className={s.resizeHandle} />
          <Panel>
            <div className={s.mainPanel}>
              {selected ? (
                <RequestPane
                  endpoint={selected}
                  onResponse={record}
                  isHistorySidebarVisible={isHistorySidebarVisible}
                  onToggleHistorySidebar={() =>
                    setIsHistorySidebarVisible(v => !v)
                  }
                />
              ) : (
                <section className={s.decriptionRoot}>
                  <Flex
                    align="center"
                    justify="space-between"
                    gap="large"
                    className={s.header}
                  >
                    <Typography.Title level={3} className={s.title}>
                      {t("components.sandboxSidebar.labels.sandbox")}
                    </Typography.Title>
                    <Button
                      onClick={() => setIsHistorySidebarVisible(v => !v)}
                      data-testid="recent-requests-button"
                    >
                      {recentRequestsLabel}
                    </Button>
                  </Flex>
                  <p className={s.description}>
                    {t("labels.explorerHomepageDescription")}
                  </p>
                </section>
              )}
              {isHistorySidebarVisible && (
                <RecentRequests
                  entries={entries}
                  onSelect={handleSelectEntry}
                />
              )}
            </div>
          </Panel>
        </Group>
      ) : (
        <div
          style={{
            padding: 40,
            textAlign: "center",
            color: "var(--ant-color-text-tertiary)",
          }}
        >
          {isLoading
            ? t("components.explorer.loadingEndpoints")
            : t("components.explorer.noEndpointsAvailable")}
        </div>
      )}
    </div>
  );
};
